// libs
import React, { useCallback, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Calendar2Outline, Close } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { LinkButton, Tooltip } from '@lyearn/ui';
import { Stack } from '@lyearn/ui';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
// components
// types
import {
  ContentEntity,
  DueDateAssignmentConfigInput,
  SectionChildResourceConfigsInputType,
} from '@/types/schema';

import ContentItem from '../ContentItem';
import SetDueDateDialog from '../Sections/actionDialog';
import { contentStyles, disabledContentStyles } from './style';
function ContentItemContainer({
  content,
  onDelete,
  disabled,
  Actions,
  isOptional,
  isMandatory,
  childResourceConfig,
  onDueDateChange,
  initialChildResourceConfig,
}: {
  content: ContentEntity;
  onDelete?: (childResourceId: string) => void;
  disabled?: boolean;
  Actions?: JSX.Element;
  isOptional?: boolean;
  isMandatory?: boolean;
  childResourceConfig?: SectionChildResourceConfigsInputType;
  initialChildResourceConfig?: SectionChildResourceConfigsInputType;
  onDueDateChange?: (childResourceId: string, dueDateConfig: DueDateAssignmentConfigInput) => void;
}) {
  const { t } = useTranslation(['Path']);
  const _onDelete = useCallback(() => onDelete?.(content.id), [onDelete, content.id]);
  const [isDueDateDialogOpen, { on, off, toggle }] = useToggle(false);
  const { [FeatureFlags.EnablePathEditorDueDate]: isSetDueDateEnabled } = useFeatureFlags();
  const dueDateConfig = useMemo(() => {
    return childResourceConfig?.dueDateConfig;
  }, [childResourceConfig]);

  const initialDueDateConfig = useMemo(() => {
    return initialChildResourceConfig?.dueDateConfig;
  }, [initialChildResourceConfig]);

  return (
    <>
      <Stack
        className={cn(disabled ? disabledContentStyles : contentStyles, 'w-full rounded-8 px-4', {
          'group hover:bg-s-info-light': !disabled,
        })}>
        <Stack
          alignItems="center"
          className="content w-full border-b border-solid border-interactive-border py-16 px-12"
          justifyContent="between">
          <ContentItem
            content={content}
            isMandatory={isMandatory}
            isOptional={isOptional}
            isOptionalContentInPath={childResourceConfig?.isOptional}
          />
          {onDueDateChange && isSetDueDateEnabled ? (
            <Tooltip placement="top" title={`${t('Path:SetDueDate.Heading')}`}>
              <LinkButton
                className="flex-shrink-0 py-2 px-4 opacity-0 transition duration-150 ease-linear group-hover:opacity-100"
                color={'internal'}
                onClick={on}>
                <Calendar2Outline />
              </LinkButton>
            </Tooltip>
          ) : null}
          {Actions || (
            <Tooltip placement="right" title={`${t('Path:RemoveContent')}`}>
              <LinkButton
                className="flex-shrink-0 py-2 px-4 opacity-0 transition duration-150 ease-linear group-hover:opacity-100"
                color={'internal'}
                disabled={disabled}
                onClick={_onDelete}>
                <Close />
              </LinkButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      {isDueDateDialogOpen && onDueDateChange ? (
        <SetDueDateDialog
          content={content}
          dueDateConfig={dueDateConfig}
          initialDueDateConfig={initialDueDateConfig}
          onClose={off}
          onDueDateChange={onDueDateChange}
        />
      ) : null}
    </>
  );
}

export default ContentItemContainer;
